import { useQuery, useTeam } from '@vrplatform/data';
import { type Maybe, day } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import {
  getListingName,
  getOwnerCompanyType,
  whereListingPeriod,
} from '@vrplatform/ui-common';
import { getContactUserAccess } from '../../views/owners/ledger/_utils';

export const STORYBOOK_OWNER_DETAIL_DRAWER_QUERY_KEY = 'owner-detail-drawer';

export function useOwnerDetailDrawerQuery(id: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  const query = useQuery(
    (q, args) => {
      if (!args.id) return null;

      return (
        q
          .contacts({
            where: {
              id: { _eq: args.id },
              type: { _eq: 'owner' },
            },
          })
          .map((contact) => {
            const status = contact.status;
            const pmsStatus = contact.pmsStatus;

            const addressData = (contact.addressData() || null) as {
              line1: string;
              line2?: string;
              city: string;
              state?: string;
              postalCode: string;
              countryCode?: string;
              stateCode?: string;
              country?: string;
            } | null;

            const address = addressData
              ? {
                  line1: addressData.line1,
                  line2: addressData.line2,
                  city: addressData.city,
                  postcode: addressData.postalCode,
                  state: addressData.state,
                  stateCode: addressData.stateCode,
                  country: addressData.country,
                  countryCode: addressData.countryCode,
                }
              : {
                  line1: '',
                  line2: undefined,
                  city: '',
                  postcode: '',
                  state: undefined,
                  stateCode: undefined,
                  country: undefined,
                  countryCode: undefined,
                };

            const defaultPayoutAccountName =
              contact.tenant
                ?.accounts({
                  where: {
                    assignments: {
                      type: { _eq: 'payout_bankAccount' },
                      tenantId: { _eq: args.teamId },
                    },
                  },
                  limit: 1,
                })
                .map((x) => x.title)[0] ?? null;

            const currentPeriodMemberships = contact
              .listingPeriodMemberships({
                where: {
                  period: whereListingPeriod({
                    date: day().yyyymmdd(),
                    type: 'ownership',
                  }),
                },
                order_by: [
                  {
                    period: {
                      listing: {
                        calculatedTitle: 'asc_nulls_last',
                      },
                    },
                  },
                ],
              })
              .map((member) => ({
                id: member.id,
                listingName: getListingName(member.period.listing),
                listingId: member.period.listingId,
                split: member.split as Maybe<number>,
              }));

            const getIsArchived = () => {
              if (status === 'inactive') {
                return true;
              }

              if (!status && pmsStatus === 'inactive') {
                return true;
              }

              return false;
            };

            return {
              id: contact.id,
              taxId: contact.taxId,
              status,
              currentPeriodMemberships,
              pmsStatus,
              isArchived: getIsArchived(),
              defaultAccountId: contact.defaultAccountId,
              tenantId: contact.tenantId,
              email: contact.email,
              phone: contact.phone,
              is1099PostalDelivery: contact.is1099PostalDelivery ?? true,
              type: contact.companyType ? 'company' : 'individual',
              companyType: getOwnerCompanyType(contact.companyType),
              firstName: contact.firstName,
              name: contact.name,
              companyName: contact.name,
              teamId: contact.tenantId,
              payoutAccountName: contact.defaultAccount?.title,
              defaultPayoutAccountName,
              connectionId: contact.connectionId,
              address,
              userAccess: getContactUserAccess(contact, args.teamId),
            };
          })[0] || null
      );
    },
    {
      skip: !id,
      queryKey: ['owners', id || ''],
      variables: {
        id,
        teamId,
      },
      storybookQueryKey: STORYBOOK_OWNER_DETAIL_DRAWER_QUERY_KEY,
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

export type OwnerDetail = NonNullable<
  ReturnType<typeof useOwnerDetailDrawerQuery>['data']
>;
